export const PSYLOG_TIMEZONE = "America/Sao_Paulo";

export type NativePsyLogIngestion = {
  substanceName: string;
  time: number;
  endTime?: number | null;
  creationDate?: number;
  dose?: number | null;
  units?: string | null;
  administrationRoute?: string | null;
  stomachFullness?: string | null;
  consumerName?: string | null;
  customUnitId?: number | null;
  notes?: string | null;
  isDoseAnEstimate?: boolean;
  estimatedDoseStandardDeviation?: number | null;
};

export type NativePsyLogExperience = {
  title?: string;
  sortDate?: number;
  creationDate?: number;
  text?: string;
  ingestions?: NativePsyLogIngestion[];
  timedNotes?: unknown[];
  ratings?: unknown[];
};

export type NativePsyLogExport = {
  experiences?: NativePsyLogExperience[];
  substanceCompanions?: unknown[];
  customSubstances?: unknown[];
  customUnits?: unknown[];
};

export type SubstanceKind = "prescrito" | "interferente" | "prn" | "opioide" | "alcool" | "manual";

export type SubstanceMapEntry = {
  input: string;
  display: string;
  brand?: string;
  kind: SubstanceKind;
  overlapHours?: number;
};

export type StomachContext = {
  raw: string | null;
  label: string;
  fullness0to10: number | null;
  mealSize: string | null;
  takenWithFood: boolean | null;
  foodContext: string | null;
};

export type NormalizedPsyLogIngestion = NativePsyLogIngestion & {
  sourceExperienceIndex: number;
  sourceExperienceTitle: string;
  sourceExperienceText: string;
  patientName: string;
  canonicalInput: string;
  displayName: string;
  brandName?: string;
  kind: SubstanceKind;
  atIso: string;
  localDate: string;
  localTime: string;
  routeNormalized: string;
  stomachContext: StomachContext;
  logType: string;
  nearbyCaffeineMg: number | null;
  warnings: string[];
};

export type PsyLogDayGroup = {
  key: string;
  patientName: string;
  localDate: string;
  firstAt: string;
  lastAt: string;
  sourceTitles: string[];
  sourceTexts: string[];
  ingestions: NormalizedPsyLogIngestion[];
  warnings: string[];
};

export type PsyLogPreview = {
  experiences: number;
  ingestions: number;
  firstAt: string | null;
  lastAt: string | null;
  days: number;
  patientDays: number;
  byPatient: Record<string, number>;
  bySubstance: Record<string, number>;
  byLogType: Record<string, number>;
  warningCount: number;
  warnings: Array<{ at: string; substance: string; message: string }>;
  groups: PsyLogDayGroup[];
};

const SUBSTANCE_MAP: Record<string, SubstanceMapEntry> = {
  lisdexamfetamine: { input: "lisdexamfetamine", display: "Lisdexanfetamina", brand: "Lyberdia", kind: "prescrito", overlapHours: 14 },
  clonidine: { input: "clonidine", display: "Clonidina", kind: "prescrito", overlapHours: 12 },
  clonazepam: { input: "clonazepam", display: "Clonazepam", kind: "prescrito", overlapHours: 18 },
  caffeine: { input: "caffeine", display: "Cafeína", kind: "interferente", overlapHours: 8 },
  promethazine: { input: "promethazine", display: "Prometazina", kind: "prn", overlapHours: 12 },
  codeine: { input: "codeine", display: "Codeína", kind: "opioide", overlapHours: 12 },
  tramadol: { input: "tramadol", display: "Tramadol", kind: "opioide", overlapHours: 12 },
  oxycodone: { input: "oxycodone", display: "Oxicodona", kind: "opioide", overlapHours: 18 },
  quetiapine: { input: "quetiapine", display: "Quetiapina", kind: "prn", overlapHours: 18 },
  alcohol: { input: "alcohol", display: "Álcool", kind: "alcool", overlapHours: 8 },
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function partsAt(value: number | string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PSYLOG_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const result: Record<string, string> = {};
  for (const part of parts) result[part.type] = part.value;
  return result;
}

export function localDateKey(value: number | string | Date) {
  const p = partsAt(value);
  return `${p.year}-${p.month}-${p.day}`;
}

export function localTimeLabel(value: number | string | Date) {
  const p = partsAt(value);
  return `${p.hour}:${p.minute}:${p.second}`;
}

export function localDateLabel(value: number | string | Date) {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: PSYLOG_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
}

export function canonicalPsyLogSubstance(rawName: string): SubstanceMapEntry {
  return SUBSTANCE_MAP[normalizeText(rawName)] ?? {
    input: rawName,
    display: rawName.trim() || "Substância sem nome",
    kind: "manual",
    overlapHours: 12,
  };
}

export function mapStomachFullness(raw?: string | null): StomachContext {
  const value = String(raw ?? "").toUpperCase();
  if (value === "EMPTY") return { raw: raw ?? null, label: "vazio", fullness0to10: 0, mealSize: null, takenWithFood: false, foodContext: "estômago vazio" };
  if (value === "QUARTERFULL") return { raw: raw ?? null, label: "1/4 cheio", fullness0to10: 2.5, mealSize: null, takenWithFood: true, foodContext: "estômago aproximadamente 1/4 cheio" };
  if (value === "HALFFULL") return { raw: raw ?? null, label: "1/2 cheio", fullness0to10: 5, mealSize: null, takenWithFood: true, foodContext: "estômago aproximadamente 1/2 cheio" };
  if (value === "FULL") return { raw: raw ?? null, label: "cheio", fullness0to10: 10, mealSize: null, takenWithFood: true, foodContext: "estômago cheio" };
  return { raw: raw ?? null, label: "não informado", fullness0to10: null, mealSize: null, takenWithFood: null, foodContext: null };
}

function defaultPatientName(consumerName: string | null | undefined, defaultPatient: string, alicePatient: string) {
  const value = normalizeText(consumerName);
  if (!value) return defaultPatient;
  if (value.includes("alice")) return alicePatient;
  if (value.includes("henrique") || value.includes("rick")) return defaultPatient;
  return String(consumerName).trim();
}

function routeLabel(raw?: string | null) {
  const value = normalizeText(raw);
  if (!value || value === "oral") return "oral";
  return String(raw).toLowerCase();
}

function sameLocalDay(a: NativePsyLogIngestion, b: NativePsyLogIngestion) {
  return localDateKey(a.time) === localDateKey(b.time);
}

function classifyLogType(current: NativePsyLogIngestion, mapped: SubstanceMapEntry, prior: NativePsyLogIngestion[]) {
  if (mapped.kind === "opioide" || mapped.kind === "alcool") return "uso_nao_prescrito";
  if (mapped.kind === "interferente") return "interferente";
  if (mapped.kind === "prn") return "PRN";
  const previous = prior.at(-1);
  if (!previous) return "manutencao";
  const gapHours = (current.time - previous.time) / 3_600_000;
  if (!(gapHours >= 0 && gapHours <= (mapped.overlapHours ?? 12))) return "manutencao";
  return sameLocalDay(previous, current) ? "redose" : "sobreposicao";
}

function warningsFor(current: NativePsyLogIngestion, mapped: SubstanceMapEntry, prior: NativePsyLogIngestion[], sameMoment: NativePsyLogIngestion[]) {
  const warnings: string[] = [];
  const dose = Number(current.dose);
  const unit = String(current.units ?? "");
  const previous = prior.at(-1);
  const gapHours = previous ? (current.time - previous.time) / 3_600_000 : null;

  if (mapped.display === "Clonidina" && unit === "µg" && ![150, 300, 450].includes(dose)) {
    warnings.push(`Dose de clonidina fora do padrão predominante do arquivo: ${dose} ${unit}; preservar como fonte e revisar manualmente.`);
  }
  if (mapped.display === "Clonazepam" && ![2, 4, 6].includes(dose)) {
    warnings.push(`Dose de clonazepam incomum no arquivo: ${dose} ${unit}; pode representar fracionamento, gotas convertidas ou erro de registro.`);
  }
  if (mapped.display === "Lisdexanfetamina" && previous && gapHours != null && gapHours <= 14) {
    warnings.push(`Nova lisdexanfetamina ${gapHours.toFixed(1)} h após a anterior; classificar como redose/sobreposição para revisão clínica.`);
  }
  if (mapped.display === "Cafeína" && dose >= 400) warnings.push(`Cafeína alta registrada: ${dose} ${unit}.`);
  if (mapped.kind === "opioide") warnings.push(`Opioide não prescrito registrado (${mapped.display} ${dose} ${unit}); espelhar em redução de danos/uso de substâncias.`);
  if (mapped.display === "Tramadol" && dose >= 400) warnings.push(`Tramadol em dose muito elevada no registro-fonte: ${dose} ${unit}; risco convulsivo/serotoninérgico relevante.`);
  if (mapped.display === "Oxicodona" && dose >= 80) warnings.push(`Oxicodona em dose elevada no registro-fonte: ${dose} ${unit}; risco grave de depressão respiratória.`);
  if (sameMoment.length > 1) warnings.push(`Há ${sameMoment.length} registros da mesma substância no mesmo minuto; preservar separadamente e revisar possível fracionamento/duplicação.`);
  if (current.isDoseAnEstimate) warnings.push("Dose marcada como estimativa no arquivo PsyLog.");
  return warnings;
}

function caffeineNear(
  current: NativePsyLogIngestion,
  all: NativePsyLogIngestion[],
  patientName: string,
  defaultPatient: string,
  alicePatient: string,
) {
  if (normalizeText(current.substanceName) === "caffeine") return Number(current.dose ?? 0) || null;
  const total = all
    .filter((row) => normalizeText(row.substanceName) === "caffeine")
    .filter((row) => defaultPatientName(row.consumerName, defaultPatient, alicePatient) === patientName)
    .filter((row) => Math.abs(row.time - current.time) <= 4 * 3_600_000)
    .reduce((sum, row) => sum + Number(row.dose ?? 0), 0);
  return total > 0 ? total : null;
}

export function normalizeNativePsyLog(
  data: NativePsyLogExport,
  defaultPatient = "Henrique Ruicherlei de Santana Fernandes",
  alicePatient = "Alice Gomes Freire",
): NormalizedPsyLogIngestion[] {
  const rows: Array<NativePsyLogIngestion & { sourceExperienceIndex: number; sourceExperienceTitle: string; sourceExperienceText: string }> = [];
  (data.experiences ?? []).forEach((experience, sourceExperienceIndex) => {
    for (const ingestion of experience.ingestions ?? []) {
      rows.push({
        ...ingestion,
        sourceExperienceIndex,
        sourceExperienceTitle: experience.title || localDateLabel(ingestion.time),
        sourceExperienceText: experience.text || "",
      });
    }
  });
  rows.sort((a, b) => a.time - b.time);

  const priorByPatientSubstance = new Map<string, NativePsyLogIngestion[]>();
  return rows.map((row) => {
    const patientName = defaultPatientName(row.consumerName, defaultPatient, alicePatient);
    const mapped = canonicalPsyLogSubstance(row.substanceName);
    const priorKey = `${normalizeText(patientName)}|${normalizeText(mapped.display)}`;
    const prior = priorByPatientSubstance.get(priorKey) ?? [];
    const sameMoment = rows.filter((candidate) =>
      Math.abs(candidate.time - row.time) < 60_000
      && canonicalPsyLogSubstance(candidate.substanceName).display === mapped.display
      && defaultPatientName(candidate.consumerName, defaultPatient, alicePatient) === patientName
    );
    const rowWarnings = warningsFor(row, mapped, prior, sameMoment);
    if (mapped.kind === "prescrito" && normalizeText(patientName) !== normalizeText(defaultPatient)) {
      rowWarnings.push(`Registro atribuído a ${patientName}; preservar a ingestão, mas não presumir tratamento ativo sem revisão clínica.`);
    }
    const normalized: NormalizedPsyLogIngestion = {
      ...row,
      patientName,
      canonicalInput: mapped.input,
      displayName: mapped.display,
      brandName: mapped.brand,
      kind: mapped.kind,
      atIso: new Date(row.time).toISOString(),
      localDate: localDateKey(row.time),
      localTime: localTimeLabel(row.time),
      routeNormalized: routeLabel(row.administrationRoute),
      stomachContext: mapStomachFullness(row.stomachFullness),
      logType: classifyLogType(row, mapped, prior),
      nearbyCaffeineMg: caffeineNear(row, rows, patientName, defaultPatient, alicePatient),
      warnings: rowWarnings,
    };
    priorByPatientSubstance.set(priorKey, [...prior, row]);
    return normalized;
  });
}

export function groupNormalizedPsyLog(rows: NormalizedPsyLogIngestion[]): PsyLogDayGroup[] {
  const groups = new Map<string, NormalizedPsyLogIngestion[]>();
  for (const row of rows) {
    const key = `${normalizeText(row.patientName)}|${row.localDate}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.entries()]
    .map(([key, ingestions]) => {
      const sorted = [...ingestions].sort((a, b) => a.time - b.time);
      return {
        key,
        patientName: sorted[0].patientName,
        localDate: sorted[0].localDate,
        firstAt: sorted[0].atIso,
        lastAt: sorted.at(-1)!.atIso,
        sourceTitles: [...new Set(sorted.map((row) => row.sourceExperienceTitle).filter(Boolean))],
        sourceTexts: [...new Set(sorted.map((row) => row.sourceExperienceText).filter(Boolean))],
        ingestions: sorted,
        warnings: [...new Set(sorted.flatMap((row) => row.warnings))],
      };
    })
    .sort((a, b) => new Date(a.firstAt).getTime() - new Date(b.firstAt).getTime());
}

export function buildNativePsyLogPreview(
  data: NativePsyLogExport,
  defaultPatient = "Henrique Ruicherlei de Santana Fernandes",
  alicePatient = "Alice Gomes Freire",
): PsyLogPreview {
  const rows = normalizeNativePsyLog(data, defaultPatient, alicePatient);
  const groups = groupNormalizedPsyLog(rows);
  const byPatient: Record<string, number> = {};
  const bySubstance: Record<string, number> = {};
  const byLogType: Record<string, number> = {};
  const warnings: PsyLogPreview["warnings"] = [];
  for (const row of rows) {
    byPatient[row.patientName] = (byPatient[row.patientName] ?? 0) + 1;
    bySubstance[row.displayName] = (bySubstance[row.displayName] ?? 0) + 1;
    byLogType[row.logType] = (byLogType[row.logType] ?? 0) + 1;
    for (const message of row.warnings) warnings.push({ at: row.atIso, substance: row.displayName, message });
  }
  return {
    experiences: data.experiences?.length ?? 0,
    ingestions: rows.length,
    firstAt: rows[0]?.atIso ?? null,
    lastAt: rows.at(-1)?.atIso ?? null,
    days: new Set(rows.map((row) => row.localDate)).size,
    patientDays: groups.length,
    byPatient,
    bySubstance,
    byLogType,
    warningCount: warnings.length,
    warnings,
    groups,
  };
}

export function isNativePsyLog(value: unknown): value is NativePsyLogExport {
  return !!value && typeof value === "object" && Array.isArray((value as NativePsyLogExport).experiences);
}
